<template>
    <!-- Step Description -->
    <div class="step-description">{{t('join.createAccount.step2.description')}}</div>
    <multiple-file-uploader
    :uploaderParams="uploaderParams"
    :uploadAction="uploadAction"
    :deleteAction="deleteAction"
    @fileAdded="fileAddedHandler"
    @fileRemoved="fileRemovedHandler"/>
</template>

<script setup>
import MultipleFileUploader from '@/components/generic/uploader/multipleUploader/MultipleFileUploader'

/* Store */
import { moduleMapper } from '@/store/helpers/modules.map'
import { requestUploadedDocument } from '@/network/api/customers/creation.api.js'

/* Vue */
import { ref, watch } from 'vue'
import { getEmptyFieldsIdentifiers } from '@/assets/scripts/objects'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

const emit = defineEmits([
    'updateFormData',
    'applyLoader'
])

/** Props
@processId {String}
@formData {Object} - Object with form data
@mandatoryList {Array} - List of mandatory fields ids/keys
@showMandatoryErrors {Boolean} - Prop to trigger display of inputs errors
*/
const props = defineProps({
    processId: [String, Number],
    formData: Object,
    mandatoryList: Array,
    showMandatoryErrors: Boolean
})

const uploadAction = ref(`${moduleMapper.JOIN_CREATE_ACCOUNT}/createDocument`)

const deleteAction = ref(`${moduleMapper.JOIN_CREATE_ACCOUNT}/deleteDocument`)

const uploaderParams = ref([{
    uploaderId: 1,
    mandatory: true,
    errorState: false,
    errorMessage: '',
    label: t('join.createAccount.step2.frontUploader.label'),
    galleryOptionsTitle: t('join.createAccount.step2.frontUploader.galleryOptionsTitle'),
    cameraButtonLabel: t('join.createAccount.step2.frontUploader.cameraButtonLabel'),
    importButtonLabel: t('join.createAccount.step2.frontUploader.importButtonLabel'),
    cameraTitle: t('join.createAccount.step2.frontUploader.cameraTitle'),
    cameraMessage: t('join.createAccount.step2.frontUploader.cameraMessage'),
    cameraPreviewMessage: t('join.createAccount.step2.frontUploader.cameraPreviewMessage'),
    placeholder: t('join.createAccount.step2.frontUploader.placeholder'),
    fileName: props.formData.frontFileName ? props.formData.frontFileName : t('join.createAccount.step2.frontUploader.fileName'),
    removeFile: false,
    file: '',
    uploadPayload: {
        processId: props.processId,
        documentType: 'front'
    }
},
{
    uploaderId: 2,
    mandatory: true,
    errorState: false,
    errorMessage: '',
    label: t('join.createAccount.step2.backUploader.label'),
    galleryOptionsTitle: t('join.createAccount.step2.backUploader.galleryOptionsTitle'),
    cameraButtonLabel: t('join.createAccount.step2.backUploader.cameraButtonLabel'),
    importButtonLabel: t('join.createAccount.step2.backUploader.importButtonLabel'),
    cameraTitle: t('join.createAccount.step2.backUploader.cameraTitle'),
    cameraMessage: t('join.createAccount.step2.backUploader.cameraMessage'),
    cameraPreviewMessage: t('join.createAccount.step2.backUploader.cameraPreviewMessage'),
    placeholder: t('join.createAccount.step2.backUploader.placeholder'),
    fileName: props.formData.backFileName ? props.formData.backFileName : t('join.createAccount.step2.backUploader.fileName'),
    removeFile: false,
    file: '',
    uploadPayload: {
        processId: props.processId,
        documentType: 'back'
    }
}])

const fileAddedHandler = event => {
    if (event.id === 1) {
        uploaderParams.value[0].errorState = false
        uploaderParams.value[0].errorMessage = ''
        emit('updateFormData', { field: 'frontFile', inputValue: event.file })
        emit('updateFormData', { field: 'frontFileName', inputValue: event.name })
    } else {
        uploaderParams.value[1].errorState = false
        uploaderParams.value[1].errorMessage = ''
        emit('updateFormData', { field: 'backFile', inputValue: event.file })
        emit('updateFormData', { field: 'backFileName', inputValue: event.name })
    }
}

const fileRemovedHandler = event => {
    if (event.id === 1) {
        emit('updateFormData', { field: 'frontFile', inputValue: '' })
        emit('updateFormData', { field: 'frontFileName', inputValue: '' })
    } else {
        emit('updateFormData', { field: 'backFile', inputValue: '' })
        emit('updateFormData', { field: 'backFileName', inputValue: '' })
    }
}

const checkIfDocumentsExist = async () => {
	emit('applyLoader', true)

    const payloadFront = {
		processId: props.processId,
		documentType: 'front'
	}

	const payloadBack = {
		processId: props.processId,
		documentType: 'back'
	}
    /* We are using a Promise all to save sometime, calling all api's at the same time, since they dont depend on eachother */
    Promise.all([
        requestUploadedDocument(payloadFront),
        requestUploadedDocument(payloadBack)
    ]).then(response => {
        if (response[0]) {
            uploaderParams.value[0].file = response[0]
            emit('updateFormData', { field: 'frontFile', inputValue: response[0] })
        }

        if (response[1]) {
            uploaderParams.value[1].file = response[1]
            emit('updateFormData', { field: 'backFile', inputValue: response[1] })
        }

        emit('applyLoader', false)
    }).catch((error) => {
        console.log(error, 'ERROR')
        emit('applyLoader', false)
    })
}

/**********************************************************************
*                                                                     *
*                          Mandatory Fields                           *
*                                                                     *
***********************************************************************/
const showMandatoryErrorsHandler = () => {
   /* Logic to scroll to first emptyField */
	const mandatoryFieldsEmptyList = getEmptyFieldsIdentifiers(props.formData, props.mandatoryList)

    if (mandatoryFieldsEmptyList.length) {
        mandatoryFieldsEmptyList.forEach(fieldName => {
            if (fieldName === 'frontFile') {
                uploaderParams.value[0].errorState = true
                uploaderParams.value[0].errorMessage = t('mandatoryFieldError')
            }
            if (fieldName === 'backFile') {
                uploaderParams.value[1].errorState = true
                uploaderParams.value[1].errorMessage = t('mandatoryFieldError')
            }
        })
    }
}

watch(() => props.showMandatoryErrors, newValue => {
	if (newValue) showMandatoryErrorsHandler()
    emit('resetShowMandatoryErrorsProp')
})

if (!!props.formData.frontFile.length || !!props.formData.backFile.length) {
    checkIfDocumentsExist()
}
</script>

<style lang="scss" scoped>
.step-description {
    @include H6-Regular;
    color: var(--secondary-dark-2);
    line-height: 18px;

    .selected-image, .default-image {
        width: 120px;
        height: 120px;
        border-radius: 50%;

        object-fit: cover;
    }
}
</style>
